import React, { useState } from 'react';
import Dropzone from 'react-dropzone';
import styled from 'styled-components';
import HButton from '../common/HButton';
import * as xlsx from 'xlsx';
import Spinner from '../common/Spinner';

const Container = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
  min-width: 991px;
  align-items: center;
  flex-direction: column;
  background-color: transparent;
  padding-top: 60px;
  padding-bottom: 60px;
  box-sizing: border-box;
  overflow: hidden;

  .title {
    font-size: 36px;
    font-weight: 600;
    color: #59616a;
    margin-bottom: 40px;
  }
`;

const ContentBox = styled.div`
  display: flex;
  width: 100%;
  max-width: 800px;
  margin-top: 30px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const DownloadTemplate = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const RectangleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  width: 100%;
  min-height: 150px;
  border-width: 2px;
  border-radius: 8px;
  border-color: #eeeeee;
  border-style: dashed;
  background-color: #fafafa;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: #f0f0f0;
  }

  p {
    font-size: 20px;
    color: #59616a;
  }
`;

const CheckBox = styled.div`
  display: flex;
  width: 100%;
  margin-top: 20px;
  align-items: center;
  justify-content: center;

  input[type='checkbox'] {
    margin-right: 10px;
    width: 18px;
    height: 18px;
  }

  label {
    font-size: 18px;
    color: #59616a;
    cursor: pointer;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  font-size: 18px;
  color: #59616a;
`;

const ResultBox = styled.div`
  margin-top: 40px;
  color: #28a745;
  font-size: 18px;
  text-align: center;
`;

const ErrorBox = styled.div`
  margin-top: 40px;
  color: #dc3545;
  font-size: 18px;
  text-align: center;
`;

const ReleaseRegistrationUpload = () => {
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const onClickDownloadTemplateExcel = () => {
    const token = localStorage.getItem('token');
    fetch('/heclore/v1/cs/versioning/releases/template/', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'ReleaseRegistrationTemplate.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        setError(`Failed to download template file: ${error.message}`);
      });
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setError(null);
    setResult(null);
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = xlsx.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          setError('No sheets found in the Excel file.');
          setLoading(false);
          return;
        }
        const rows = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName], {
          header: 1,
          blankrows: false,
        });

        if (!rows || rows.length < 2) {
          setError('The Excel file is empty or does not contain data rows.');
          setLoading(false);
          return;
        }

        const expectedHeaders = ['global version', 'scan3d version', 'patientdb version', 'os', 'gpu', 'model', 'installerlink', 'changeloglink'];
        const headerRow = rows[0].map(h => String(h).toLowerCase().trim());

        const requiredHeaders = expectedHeaders.slice(0, 6); // Les 6 premiers sont obligatoires
        const missingRequired = requiredHeaders.filter(h => !headerRow.includes(h));
        if (missingRequired.length > 0) {
          setError(`Invalid Excel format. Missing headers: ${missingRequired.join(', ')}`);
          setLoading(false);
          return;
        }

        const indices = {
          globalVersion: headerRow.indexOf('global version'),
          scan3dVersion: headerRow.indexOf('scan3d version'),
          patientDbVersion: headerRow.indexOf('patientdb version'),
          os: headerRow.indexOf('os'),
          gpu: headerRow.indexOf('gpu'),
          model: headerRow.indexOf('model'),
          installerLink: headerRow.indexOf('installerlink'),
          changelogLink: headerRow.indexOf('changeloglink'),
        };

        const releases = [];

        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.length === 0) continue;

          const globalVersion = String(row[indices.globalVersion] || '').trim();
          const scan3dVersion = String(row[indices.scan3dVersion] || '').trim();
          const patientDbVersion = String(row[indices.patientDbVersion] || '').trim();
          const os = String(row[indices.os] || '').trim();
          const gpu = String(row[indices.gpu] || '').trim();
          const model = String(row[indices.model] || '').trim();
          
          const installerLink = indices.installerLink !== -1 ? String(row[indices.installerLink] || '').trim() : '';
          const changelogLink = indices.changelogLink !== -1 ? String(row[indices.changelogLink] || '').trim() : '';

          const links = [];
          if (installerLink) {
            if (!/^https?:\/\//i.test(installerLink)) {
              setError(`Row ${i + 1}: invalid URL in InstallerLink column.`);
              setLoading(false);
              return;
            }
            links.push(installerLink);
          }
          if (changelogLink) {
            if (!/^https?:\/\//i.test(changelogLink)) {
              setError(`Row ${i + 1}: invalid URL in ChangeLogLink column.`);
              setLoading(false);
              return;
            }
            links.push(changelogLink);
          }
          const combinedLink = links.join(';');

          if (!globalVersion || !scan3dVersion || !patientDbVersion || !os || !gpu || !model) {
            setError(`Row ${i + 1} is incomplete. All required columns must be filled.`);
            setLoading(false);
            return;
          }

          releases.push({
            globalVersion,
            scan3dVersion,
            patientDbVersion,
            os,
            gpu,
            model,
            link: combinedLink,
          });
        }

        if (releases.length === 0) {
          setError('No valid releases found in the Excel file.');
          setLoading(false);
          return;
        }

        // API Call to backend
        const token = localStorage.getItem('token');
        fetch('/heclore/v1/cs/versioning/releases/upload/', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ releases, overwrite: checked }),
        })
        .then(response => {
          if (!response.ok) {
            return response.json().then(errData => {
              throw new Error(errData.message || `API Error: ${response.statusText} (Status: ${response.status})`);
            }).catch(() => {
              throw new Error(`API Error: ${response.statusText} (Status: ${response.status})`);
            });
          }
          return response.json();
        })
        .then(apiResult => {
          let successMsg = '';
          let skippedMsg = '';

          const skippedCount = apiResult.skipped ? apiResult.skipped.length : 0;
          const totalCount = releases.length;
          const successCount = totalCount - skippedCount;

          if (successCount > 0) {
            successMsg = `Successfully uploaded ${successCount} release(s).`;
          }

          if (apiResult.skipped && apiResult.skipped.length > 0) {
            skippedMsg =
              'Some releases are invalid:\n' +
              apiResult.skipped
                .map(item => {
                  let reason = item.reason;
                  return `Global Version: ${item.globalVersion} - ${reason}`;
                })
                .join('\n');
          }

          if (successMsg && skippedMsg) {
            setResult(`${successMsg}\n\n${skippedMsg}`);
          } else if (successMsg) {
            setResult(successMsg);
          } else if (skippedMsg) {
            setResult(skippedMsg);
          }

          setLoading(false);
        })
        .catch(apiError => {
          setError(`Upload failed: ${apiError.message}`);
          setLoading(false);
        });

      } catch (parseError) {
        setError(`Error processing Excel file: ${parseError.message}`);
        setLoading(false);
      }
    };
    reader.onerror = () => {
      setError('Failed to read the file. Please ensure it is a valid .xls or .xlsx file.');
      setLoading(false);
    };
    reader.readAsArrayBuffer(acceptedFiles[0]);
  };

  if (loading) {
    return (
      <Container>
        <div className="title">Release Upload</div>
        <LoadingContainer>
          <Spinner />
          <p style={{ marginTop: '20px' }}>Processing your file...</p>
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="title">Release Upload</div>
        <ErrorBox>{error}</ErrorBox>
        <HButton
          category="Primary"
          size="Medium"
          text="Try Again"
          onClick={() => setError(null)}
        />
      </Container>
    );
  }

  if (result) {
    return (
      <Container>
        <div className="title">Release Upload</div>
        <ResultBox>{result}</ResultBox>
        <div style={{ display: 'flex', marginTop: '20px', gap: '10px' }}>
          <HButton
            category="Primary"
            size="Medium"
            text="Register More"
            onClick={() => {
              setResult(null);
              setError(null);
            }}
          />
          <HButton
            category="Secondary"
            size="Medium"
            text="Return to Releases"
            onClick={() => {
              window.location.href = '/adm/versioning/release';
            }}
          />
        </div>
      </Container>
    );
  }

  return (
    <Dropzone onDrop={onDrop} accept=".xls,.xlsx" noClick={true}>
      {({ getRootProps, getInputProps, open }) => (
        <Container>
          <div className="title">Release Registration Upload</div>
          <ContentBox>
            <DownloadTemplate>
              <HButton
                category="Tertiary"
                size="Medium"
                text={'Download release template'}
                onClick={onClickDownloadTemplateExcel}
              />
            </DownloadTemplate>
            <RectangleBox {...getRootProps({ className: 'dropzone' })} onClick={open}>
              <input {...getInputProps()} />
              <p>Drag and drop release template file here or click to select</p>
            </RectangleBox>
            <CheckBox>
              <input
                type="checkbox"
                id="overwriteReleaseCheckbox"
                checked={checked}
                onChange={() => setChecked(!checked)}
              />
              <label htmlFor="overwriteReleaseCheckbox">
                Do you want to overwrite release information?
              </label>
            </CheckBox>
          </ContentBox>
        </Container>
      )}
    </Dropzone>
  );
};

export default ReleaseRegistrationUpload;